import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { useId, useIsLoading } from '../../page-framework/panel/Panel';
import { PanelEditingActions } from '../../page-framework/panel/PanelActions';
import {
    MATRIX_PANEL_PREVIEW_IMAGE,
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import QueryMatrix from '../../matrix/QueryMatrix';
import SiteDetail from './SiteDetail';
import { useTimeRangeSelection } from '../../charts/status-breakout/UseTimeRangeSelection';
import { FilterStateContainer } from '../../../stores/FilterStateStore';
import { siteFilterId, timeRangeFilterId, useSite } from '../../filters';
import { useDataService } from '../../../services/ApiDataService';
import MatrixDisplayOptionsAction, { useStatusTrendVisibility } from '../../matrix/internal/MatrixDisplayOptionsAction';

const title = t`Site Service Tests by nPoint`;

function SiteDetailPanel({ flexHeight }) {
    const id = useId();
    const [, /* Not used */ setIsLoading] = useIsLoading();
    const site = useSite();
    const showMatrix = site?.hasPulses === true;
    const [timeRangeSelection, setTimeRangeSelection] = useState(undefined);
    const showStatusTrend = useStatusTrendVisibility(setTimeRangeSelection);
    const matrixFilterState = useTimeRangeSelection(timeRangeSelection, showStatusTrend);
    const dataService = useDataService();

    useEffect(() => {
        // The status trend will set the panel's loading flag when it loads data.  We need to be sure that
        // the loading flag gets cleared when the status trend is hidden.  Otherwise, the loading indicator
        // will be displayed indefinitely if the status trend was in the process of loading data when it
        // was hidden.
        if (!showStatusTrend) setIsLoading(false);
    }, [showStatusTrend, setIsLoading]);

    if (!site) return null;

    return (
        <>
            <PanelEditingActions>
                <MatrixDisplayOptionsAction />
            </PanelEditingActions>
            {showStatusTrend && <SiteDetail onTimeRangeChange={setTimeRangeSelection} />}
            {showMatrix && !!matrixFilterState && (
                <FilterStateContainer
                    name={`${id}-matrix-filter-state`}
                    dataService={dataService}
                    manageUrl={false}
                    state={matrixFilterState}
                >
                    <QueryMatrix visibleRows={flexHeight ? 0 : 10} pivot="siteWorstTestCounts" />
                </FilterStateContainer>
            )}
        </>
    );
}

SiteDetailPanel.propTypes = panelPropTypes;

SiteDetailPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
};

export default Panel({
    typeAlias: 'SiteMatrix',
    catalogEntry: {
        category: PANEL_CATEGORIES.SITE,
        priority: PANEL_PRIORITIES.SUBLIST,
        title,
        description: t`Status matrix for nPoints by service tests in a selected site`,
        previewImage: MATRIX_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId, siteFilterId],
        optional: [],
    },
})(SiteDetailPanel);
